import { Form, Input, App, Button } from 'antd'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { supabase } from '../../utils/supabase'
import { useAuthContext } from '../../context/auth'
import { FormContainer, StyledFormItem, FormActions } from './styles/ChangePasswordFormStyles'

interface ChangePasswordFormValues {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export function ChangePasswordForm() {
  const { notification } = App.useApp()
  const { user } = useAuthContext()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    if (!user?.email) {
      notification.error({
        title: 'Error',
        description: 'Unable to verify user email',
      })
      return
    }

    setLoading(true)

    try {
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: values.currentPassword,
      })

      if (verifyError) {
        notification.error({
          title: 'Incorrect Password',
          description: 'Your current password is incorrect',
        })
        setLoading(false)
        return
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: values.newPassword,
      })

      if (updateError) {
        notification.error({
          title: 'Update Failed',
          description: updateError.message,
        })
      } else {
        notification.success({
          title: 'Password Changed',
          description: 'Your password has been updated successfully',
        })
        form.resetFields()
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'settings', action: 'change_password' },
      })
      notification.error({
        title: 'Error',
        description: 'An unexpected error occurred',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormContainer>
      <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
        <StyledFormItem
          name="currentPassword"
          label="Current Password"
          rules={[{ required: true, message: 'Please enter your current password' }]}
        >
          <Input.Password placeholder="Enter your current password" size="large" />
        </StyledFormItem>

        <StyledFormItem
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Please enter a new password' },
            { min: 8, message: 'Password must be at least 8 characters' },
          ]}
        >
          <Input.Password placeholder="Enter your new password" size="large" />
        </StyledFormItem>

        <StyledFormItem
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('Passwords do not match'))
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm your new password" size="large" />
        </StyledFormItem>

        <FormActions>
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Password
          </Button>
        </FormActions>
      </Form>
    </FormContainer>
  )
}
